import { addProtocol } from 'maplibre-gl'
import { getTile, putTile, deleteTilesForTour } from './db'
import { alongLine, lineDistanceMeters, type LngLatEle } from './geo'

export const CACHED_PROTOCOL = 'cached'

/** Web-mercator tile containing a point. */
export function lngLatToTile(lng: number, lat: number, z: number): { x: number; y: number } {
  const n = 2 ** z
  const x = Math.floor(((lng + 180) / 360) * n)
  const latRad = (lat * Math.PI) / 180
  const y = Math.floor(((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n)
  return { x: Math.min(n - 1, Math.max(0, x)), y: Math.min(n - 1, Math.max(0, y)) }
}

/** Tiles covering a corridor around the route, with a 1-tile buffer, for z in [minZ, maxZ]. */
export function tilesForRoute(coords: readonly LngLatEle[], minZ = 6, maxZ = 14): { z: number; x: number; y: number }[] {
  const out: { z: number; x: number; y: number }[] = []
  const totalM = lineDistanceMeters(coords)
  for (let z = minZ; z <= maxZ; z++) {
    const seen = new Set<string>()
    // sample the line densely enough that no tile is skipped (tile width ≥ ~2.4km at z14 mid-lat)
    const tileM = (40075000 / 2 ** z) * Math.cos((coords[0][1] * Math.PI) / 180)
    const step = Math.max(50, tileM / 3)
    for (let d = 0; d <= totalM; d += step) {
      const p = alongLine(coords, d)
      const { x, y } = lngLatToTile(p[0], p[1], z)
      const buffer = z >= 12 ? 1 : 0
      for (let dx = -buffer; dx <= buffer; dx++) {
        for (let dy = -buffer; dy <= buffer; dy++) {
          const key = `${x + dx}/${y + dy}`
          if (!seen.has(key)) {
            seen.add(key)
            out.push({ z, x: x + dx, y: y + dy })
          }
        }
      }
    }
  }
  return out
}

let tileTemplates: string[] | null = null

/**
 * Resolve the style's vector tile URL template(s) once (TileJSON indirection),
 * so downloads and the cached:// protocol agree on real URLs.
 */
export async function resolveTileTemplates(styleUrl: string): Promise<string[]> {
  if (tileTemplates) return tileTemplates
  const style = (await (await fetch(styleUrl)).json()) as {
    sources: Record<string, { type: string; url?: string; tiles?: string[] }>
  }
  const templates: string[] = []
  for (const src of Object.values(style.sources)) {
    if (src.type !== 'vector') continue
    if (src.tiles) templates.push(...src.tiles)
    else if (src.url) {
      const tj = (await (await fetch(src.url)).json()) as { tiles?: string[] }
      if (tj.tiles) templates.push(...tj.tiles)
    }
  }
  tileTemplates = templates
  return templates
}

/** Register the cached:// protocol: IndexedDB first, network fallback. */
export function setupOfflineProtocol(): void {
  addProtocol(CACHED_PROTOCOL, async (params, abortController) => {
    const url = params.url.replace(`${CACHED_PROTOCOL}://`, '')
    const stored = await getTile(url)
    if (stored) return { data: stored.data }
    const res = await fetch(url, { signal: abortController?.signal })
    if (!res.ok) throw new Error(`tile fetch failed: ${res.status}`)
    return { data: await res.arrayBuffer() }
  })
}

export interface DownloadProgress {
  done: number
  total: number
  bytes: number
}

/** Download all corridor tiles for a tour into IndexedDB. Resumable: skips stored tiles. */
export async function downloadTilesForTour(
  tourId: string,
  coords: readonly LngLatEle[],
  styleUrl: string,
  onProgress: (p: DownloadProgress) => void,
  signal?: AbortSignal,
): Promise<DownloadProgress> {
  const templates = await resolveTileTemplates(styleUrl)
  if (!templates.length) throw new Error('no tile sources found in map style')
  const tiles = tilesForRoute(coords)
  const urls = tiles.flatMap((t) =>
    templates.map((tpl) => tpl.replace('{z}', String(t.z)).replace('{x}', String(t.x)).replace('{y}', String(t.y))),
  )
  const progress: DownloadProgress = { done: 0, total: urls.length, bytes: 0 }

  const queue = [...urls]
  const workers = Array.from({ length: 6 }, async () => {
    while (queue.length) {
      if (signal?.aborted) throw new DOMException('aborted', 'AbortError')
      const url = queue.shift()!
      const existing = await getTile(url)
      if (!existing) {
        try {
          const res = await fetch(url, { signal })
          // 204/404 = empty tile (ocean etc.) — store a marker so we don't refetch
          const data = res.ok ? await res.arrayBuffer() : new ArrayBuffer(0)
          await putTile(url, data, res.headers.get('content-type') ?? 'application/x-protobuf', tourId)
          progress.bytes += data.byteLength
        } catch (e) {
          if (e instanceof DOMException && e.name === 'AbortError') throw e
          // single failed tile shouldn't kill the download
        }
      }
      progress.done++
      if (progress.done % 10 === 0 || progress.done === progress.total) onProgress({ ...progress })
    }
  })
  await Promise.all(workers)
  return progress
}

export { deleteTilesForTour }

/** Ask the browser to protect our storage from eviction (installed PWAs usually granted). */
export async function requestPersistentStorage(): Promise<boolean> {
  try {
    if (navigator.storage?.persisted && (await navigator.storage.persisted())) return true
    return (await navigator.storage?.persist?.()) ?? false
  } catch {
    return false
  }
}
